import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmployerPlanDesignatedUnit } from '../../../../../../models/employers/plans/employer-plan-designated-unit';
import { EmployerPlanDesignatedUnitVersion } from '../../../../../../models/employers/plans/employer-plan-designated-unit-version';
import { DateUtils } from '../../../../../../utils/date-utils';

@Component({
  selector: 'app-designated-units-table-row',
  templateUrl: './designated-units-table-row.component.html',
  styleUrls: ['./designated-units-table-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DesignatedUnitsTableCardTableRowComponent implements OnChanges {
  @Input() unit!: EmployerPlanDesignatedUnit;
  @Input() isLoading: boolean | null = false;
  @Output() rowClicked = new EventEmitter<void>();

  private _unit = new BehaviorSubject<EmployerPlanDesignatedUnit | null>(null);
  private unit$ = this._unit as Observable<EmployerPlanDesignatedUnit | null>;

  private latestVersion$ = this.unit$.pipe(map(unit => this.getLatestVersion(unit?.versions)));

  private planHasExpiryDate$ = this.latestVersion$.pipe(map(version => !!version && !!version.endDate));

  private planIsScheduled$ = this.latestVersion$.pipe(
    map(version => !!version && new Date(version.startDate).getTime() > new Date().getTime())
  );

  public expiryTooltip$ = combineLatest([this.planHasExpiryDate$, this.latestVersion$]).pipe(
    map(([hasExpiryDate, version]) => {
      return hasExpiryDate
        ? $localize`This Designated Unit will expire on ${DateUtils.formatDateStringToCurrentDate(
            version?.endDate ?? '-'
          )}`
        : null;
    })
  );

  public scheduledTooltip$ = combineLatest([this.planIsScheduled$, this.latestVersion$]).pipe(
    map(([isScheduled, version]) => {
      return isScheduled
        ? $localize`This Designated Unit will become active on ${DateUtils.formatDateStringToCurrentDate(
            version?.startDate ?? '-'
          )}`
        : null;
    })
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.unit) {
      this._unit.next(this.unit);
    }
  }

  private getLatestVersion(
    versions: EmployerPlanDesignatedUnitVersion[] | undefined
  ): EmployerPlanDesignatedUnitVersion | null {
    if (!versions?.length) return null;
    const sorted = [...versions].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    return sorted[0] ?? null;
  }
}
